/**
 * GestionDemandesPartenariat.js
 * 
 * 🤝 GESTION DES DEMANDES DE PARTENARIAT 🤝
 * Interface Super Admin pour visualiser et gérer les demandes de partenariat
 * reçues via le formulaire "Devenir Partenaire" du Footer
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Handshake, Search, Filter, RefreshCw, Eye, CheckCircle, XCircle, Clock, Building2, Mail, Phone, Globe, FileText, Calendar, User, Loader2, MessageSquare } from 'lucide-react';
import PartenariatStatsPanel from './PartenariatStatsPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configuration des statuts
const STATUTS = {
  nouvelle: { label: 'Nouvelle', color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Clock },
  en_cours: { label: 'En cours', color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: RefreshCw },
  traitee: { label: 'Traitée', color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
  refusee: { label: 'Refusée', color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle }
};

export default function GestionDemandesPartenariat({ getAuthHeader }) {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchDemandes();
  }, []);

  const fetchDemandes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/demandes-partenariat`, {
        headers: getAuthHeader()
      });
      setDemandes(response.data || []);
    } catch {
      toast.error('Erreur lors du chargement des demandes');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (demandeId, newStatus) => {
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/demandes-partenariat/${demandeId}/status`,
        { status: newStatus, notes: notes || null },
        { headers: getAuthHeader() }
      );
      toast.success(`Statut mis à jour : ${STATUTS[newStatus].label}`);
      fetchDemandes();
      setShowDetailsModal(false);
      setNotes('');
    } catch {
      toast.error('Erreur lors de la mise à jour du statut');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openDetails = (demande) => {
    setSelectedDemande(demande);
    setNotes(demande.notes_admin || '');
    setShowDetailsModal(true);
  };

  const handleTogglePublication = async (demandeId, publie) => {
    setUpdatingStatus(true);
    try {
      const res = await axios.put(`${API}/demandes-partenariat/${demandeId}/publication`, { publie }, { headers: getAuthHeader() });
      toast.success(publie ? 'Partenaire publié sur l\'annuaire' : 'Partenaire dépublié');
      setSelectedDemande(res.data);
      fetchDemandes();
    } catch {
      toast.error('Erreur lors de la mise à jour de la publication');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleLogoUpload = async (demandeId, file) => {
    if (!file) return;
    if (file.size > 2_000_000) { toast.error('Logo trop volumineux (max 2 Mo)'); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const res = await axios.put(`${API}/demandes-partenariat/${demandeId}/logo`, { logo_url: reader.result }, { headers: getAuthHeader() });
        toast.success('Logo enregistré');
        setSelectedDemande(res.data);
        fetchDemandes();
      } catch {
        toast.error('Erreur lors de l\'envoi du logo');
      }
    };
    reader.readAsDataURL(file);
  };

  // Filtrage des demandes
  const filteredDemandes = demandes.filter(d => {
    const matchSearch = 
      d.raison_sociale?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      d.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchStatus = filterStatus === 'all' || d.status === filterStatus;
    
    return matchSearch && matchStatus;
  });

  // Statistiques
  const stats = {
    total: demandes.length,
    nouvelles: demandes.filter(d => d.status === 'nouvelle').length,
    en_cours: demandes.filter(d => d.status === 'en_cours').length,
    traitees: demandes.filter(d => d.status === 'traitee').length,
    refusees: demandes.filter(d => d.status === 'refusee').length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-6" data-testid="gestion-demandes-partenariat">
      {/* V36.46 — Header institutionnel ultra-pro */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0a1e3f] via-[#0B3C5D] to-[#0a1e3f] text-white shadow-xl">
        {/* Accent doré supérieur */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-[#fbbf24] to-transparent" />
        {/* Halo décoratif */}
        <div className="absolute -right-20 -top-20 w-72 h-72 rounded-full bg-[#fbbf24]/8 blur-3xl pointer-events-none" />
        <div className="absolute -left-20 -bottom-20 w-72 h-72 rounded-full bg-emerald-400/8 blur-3xl pointer-events-none" />

        <div className="relative p-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4 mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-[#fbbf24]/90 font-semibold mb-1">
                O&apos;SCOP · Partenariats
              </div>
              <h2 className="text-2xl lg:text-3xl font-bold flex items-center gap-3 leading-tight" style={{ fontFamily: '"Playfair Display", Georgia, serif' }}>
                <Handshake className="h-7 w-7 text-[#fbbf24]" />
                Demandes de Partenariat
              </h2>
              <p className="text-slate-300/80 mt-1.5 text-sm">
                Gerez les demandes recues via le formulaire « Devenir Partenaire »
              </p>
            </div>
            <Button
              onClick={fetchDemandes}
              className="bg-white/10 hover:bg-white/20 text-white border border-white/20 backdrop-blur-sm shadow-sm"
              variant="outline"
              data-testid="refresh-partenariat-btn"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Mini stats institutionnelles */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div className="bg-white/8 backdrop-blur-md rounded-xl p-3 text-center border border-white/10 shadow-sm hover:bg-white/12 transition-colors">
              <p className="text-3xl font-bold text-white" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>{stats.total}</p>
              <p className="text-[10px] text-slate-300 uppercase tracking-widest font-medium mt-1">Total</p>
            </div>
            <div className="bg-blue-500/15 backdrop-blur-md rounded-xl p-3 text-center border border-blue-300/20 shadow-sm hover:bg-blue-500/25 transition-colors">
              <p className="text-3xl font-bold text-blue-200" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>{stats.nouvelles}</p>
              <p className="text-[10px] text-blue-100 uppercase tracking-widest font-medium mt-1">Nouvelles</p>
            </div>
            <div className="bg-amber-500/15 backdrop-blur-md rounded-xl p-3 text-center border border-amber-300/20 shadow-sm hover:bg-amber-500/25 transition-colors">
              <p className="text-3xl font-bold text-amber-200" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>{stats.en_cours}</p>
              <p className="text-[10px] text-amber-100 uppercase tracking-widest font-medium mt-1">En cours</p>
            </div>
            <div className="bg-emerald-500/15 backdrop-blur-md rounded-xl p-3 text-center border border-emerald-300/20 shadow-sm hover:bg-emerald-500/25 transition-colors">
              <p className="text-3xl font-bold text-emerald-200" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>{stats.traitees}</p>
              <p className="text-[10px] text-emerald-100 uppercase tracking-widest font-medium mt-1">Traitees</p>
            </div>
            <div className="bg-red-500/15 backdrop-blur-md rounded-xl p-3 text-center border border-red-300/20 shadow-sm hover:bg-red-500/25 transition-colors">
              <p className="text-3xl font-bold text-red-200" style={{ fontFamily: '"Outfit", system-ui, sans-serif' }}>{stats.refusees}</p>
              <p className="text-[10px] text-red-100 uppercase tracking-widest font-medium mt-1">Refusees</p>
            </div>
          </div>
        </div>
      </div>

      {/* V37.12 — Mini tableau de bord (KPIs + courbe 30j) */}
      <PartenariatStatsPanel getAuthHeader={getAuthHeader} refreshKey={demandes.length} />

      {/* Filtres */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, entreprise, email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-[200px]">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="nouvelle">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500" />
                    Nouvelles
                  </span>
                </SelectItem>
                <SelectItem value="en_cours">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-amber-500" />
                    En cours
                  </span>
                </SelectItem>
                <SelectItem value="traitee">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500" />
                    Traitées
                  </span>
                </SelectItem>
                <SelectItem value="refusee">
                  <span className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500" />
                    Refusées
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Liste des demandes */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-indigo-600" />
            Demandes reçues ({filteredDemandes.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[#0B3C5D]" />
            </div>
          ) : filteredDemandes.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center mb-4">
                <Handshake className="h-10 w-10 text-slate-400" />
              </div>
              <p className="text-slate-700 text-lg font-semibold">Aucune demande de partenariat</p>
              <p className="text-slate-500 text-sm mt-1 max-w-md mx-auto">
                Les demandes recues via le formulaire « Devenir Partenaire » apparaitront ici.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredDemandes.map((demande) => {
                const StatusIcon = STATUTS[demande.status]?.icon || Clock;
                const isNew = demande.status === 'nouvelle';
                return (
                  <div
                    key={demande.id}
                    className={`relative overflow-hidden border-l-4 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer bg-white ${
                      isNew ? 'border-l-blue-500 bg-gradient-to-r from-blue-50/40 to-white' :
                      demande.status === 'en_cours' ? 'border-l-amber-500 bg-gradient-to-r from-amber-50/40 to-white' :
                      demande.status === 'traitee' ? 'border-l-emerald-500 bg-gradient-to-r from-emerald-50/40 to-white' :
                      demande.status === 'refusee' ? 'border-l-red-500 bg-gradient-to-r from-red-50/40 to-white' :
                      'border-l-slate-300'
                    }`}
                    onClick={() => openDetails(demande)}
                    data-testid={`partenariat-card-${demande.id}`}
                  >
                    {isNew && (
                      <div className="absolute top-2 right-2">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500" />
                        </span>
                      </div>
                    )}
                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#0B3C5D] to-[#0a1e3f] flex items-center justify-center flex-shrink-0 shadow-md">
                          <Building2 className="h-6 w-6 text-[#fbbf24]" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <h3 className="font-bold text-slate-900 truncate text-base">
                              {demande.raison_sociale || 'Entreprise non renseignee'}
                            </h3>
                            {demande.statut_juridique && (
                              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600">{demande.statut_juridique}</Badge>
                            )}
                            {demande.langue && (
                              <Badge variant="outline" className="text-[10px] border-slate-300 text-slate-600 uppercase">{demande.langue}</Badge>
                            )}
                          </div>
                          <p className="text-sm text-slate-700 font-medium">
                            {demande.prenom} {demande.nom}
                          </p>
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-slate-600">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3 w-3 text-slate-400" />
                              <a href={`mailto:${demande.email}`} className="hover:text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>{demande.email}</a>
                            </span>
                            {demande.telephone && (
                              <span className="flex items-center gap-1">
                                <Phone className="h-3 w-3 text-slate-400" />
                                <a href={`tel:${demande.telephone}`} className="hover:text-blue-700 hover:underline" onClick={(e) => e.stopPropagation()}>
                                  {demande.indicatif_pays ? `${demande.indicatif_pays} ` : ''}{demande.telephone}
                                </a>
                              </span>
                            )}
                            {demande.siret && (
                              <span className="flex items-center gap-1 font-mono">
                                <span className="text-slate-400">SIRET</span> {demande.siret}
                              </span>
                            )}
                            <span className="flex items-center gap-1">
                              <Calendar className="h-3 w-3 text-slate-400" />
                              {formatDate(demande.date_creation || demande.date_demande)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Badge className={`${
                          demande.status === 'nouvelle' ? 'bg-blue-700' :
                          demande.status === 'en_cours' ? 'bg-amber-700' :
                          demande.status === 'traitee' ? 'bg-emerald-700' :
                          demande.status === 'refusee' ? 'bg-red-700' : 'bg-slate-700'
                        } text-white border-0 shadow-sm px-2.5 py-1 font-semibold`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {STATUTS[demande.status]?.label}
                        </Badge>
                        <Button variant="outline" size="sm" className="text-[#0B3C5D] border-slate-300 hover:bg-slate-50 shadow-sm">
                          <Eye className="h-4 w-4 mr-1" />
                          Voir
                        </Button>
                      </div>
                    </div>
                    {demande.message && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <p className="text-sm text-slate-700 line-clamp-2 italic">
                          <MessageSquare className="h-3 w-3 inline mr-1 text-slate-400" />
                          {demande.message}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de détails */}
      <Dialog open={showDetailsModal} onOpenChange={setShowDetailsModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Handshake className="h-5 w-5 text-indigo-600" />
              Détails de la demande de partenariat
            </DialogTitle>
            <DialogDescription>
              Consultez les informations et mettez à jour le statut de la demande
            </DialogDescription>
          </DialogHeader>

          {selectedDemande && (
            <div className="space-y-6 py-4">
              {/* Statut actuel */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <div>
                  <p className="text-sm text-gray-500">Statut actuel</p>
                  <Badge className={`${STATUTS[selectedDemande.status]?.color} border px-3 py-1 mt-1`}>
                    {STATUTS[selectedDemande.status]?.label}
                  </Badge>
                </div>
                <div className="text-right">
                  <p className="text-sm text-gray-500">Reçue le</p>
                  <p className="font-medium">{formatDate(selectedDemande.date_creation)}</p>
                </div>
              </div>

              {/* Informations contact */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-indigo-800">
                    <User className="h-4 w-4" />
                    Contact
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Nom :</strong> {selectedDemande.prenom} {selectedDemande.nom}</p>
                    <p className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-gray-400" />
                      <a href={`mailto:${selectedDemande.email}`} className="text-indigo-600 hover:underline">
                        {selectedDemande.email}
                      </a>
                    </p>
                    <p className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-gray-400" />
                      {selectedDemande.telephone}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <h4 className="font-semibold flex items-center gap-2 text-purple-800">
                    <Building2 className="h-4 w-4" />
                    Entreprise
                  </h4>
                  <div className="space-y-2 text-sm">
                    <p><strong>Raison sociale :</strong> {selectedDemande.raison_sociale || '-'}</p>
                    <p><strong>SIRET :</strong> {selectedDemande.siret || 'Non renseigné'}</p>
                    <p><strong>Statut juridique :</strong> {selectedDemande.statut_juridique || '-'}</p>
                    <p className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-gray-400" />
                      {selectedDemande.pays || 'France'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Message */}
              <div className="space-y-3">
                <h4 className="font-semibold flex items-center gap-2 text-pink-800">
                  <MessageSquare className="h-4 w-4" />
                  Message de demande
                </h4>
                <div className="p-4 bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl border border-indigo-100">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">
                    {selectedDemande.message || 'Aucun message'}
                  </p>
                </div>
              </div>

              {/* Publication & logo sur l'annuaire public */}
              <div className="space-y-3 p-4 rounded-xl border border-emerald-200 bg-emerald-50/40">
                <h4 className="font-semibold flex items-center gap-2 text-emerald-800">
                  <Globe className="h-4 w-4" /> Annuaire public « Projets à soutenir »
                </h4>
                <div className="flex items-center gap-4 flex-wrap">
                  {selectedDemande.logo_url && (
                    <img src={selectedDemande.logo_url} alt="logo" className="h-14 w-14 object-contain rounded-lg border border-slate-200 bg-white" data-testid="partenariat-logo-preview" />
                  )}
                  <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm font-medium text-slate-700 hover:bg-slate-50 cursor-pointer" data-testid="partenariat-logo-upload-label">
                    <FileText className="h-4 w-4" /> {selectedDemande.logo_url ? 'Changer le logo' : 'Ajouter un logo'}
                    <input type="file" accept="image/*" className="hidden" data-testid="partenariat-logo-input" onChange={(e) => handleLogoUpload(selectedDemande.id, e.target.files?.[0])} />
                  </label>
                  <Button
                    onClick={() => handleTogglePublication(selectedDemande.id, !selectedDemande.publie)}
                    disabled={updatingStatus}
                    data-testid="partenariat-publier-btn"
                    className={selectedDemande.publie ? 'bg-slate-600 hover:bg-slate-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                  >
                    {selectedDemande.publie ? <><XCircle className="h-4 w-4 mr-1" /> Dépublier</> : <><CheckCircle className="h-4 w-4 mr-1" /> Publier</>}
                  </Button>
                  <Badge className={selectedDemande.publie ? 'bg-emerald-600 text-white' : 'bg-slate-400 text-white'}>
                    {selectedDemande.publie ? 'Publié' : 'Non publié'}
                  </Badge>
                </div>
              </div>

              {/* Notes admin */}
              <div className="space-y-3">
                <h4 className="font-semibold">Notes internes</h4>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ajoutez des notes internes sur cette demande..."
                  rows={3}
                />
              </div>

              {/* Actions de changement de statut */}
              <div className="space-y-3 pt-4 border-t">
                <h4 className="font-semibold">Changer le statut</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    className="border-blue-200 text-blue-700 hover:bg-blue-50"
                    onClick={() => handleUpdateStatus(selectedDemande.id, 'nouvelle')}
                    disabled={updatingStatus || selectedDemande.status === 'nouvelle'}
                  >
                    <Clock className="h-4 w-4 mr-1" />
                    Nouvelle
                  </Button>
                  <Button
                    variant="outline"
                    className="border-yellow-200 text-yellow-700 hover:bg-yellow-50"
                    onClick={() => handleUpdateStatus(selectedDemande.id, 'en_cours')}
                    disabled={updatingStatus || selectedDemande.status === 'en_cours'}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    En cours
                  </Button>
                  <Button
                    variant="outline"
                    className="border-green-200 text-green-700 hover:bg-green-50"
                    onClick={() => handleUpdateStatus(selectedDemande.id, 'traitee')}
                    disabled={updatingStatus || selectedDemande.status === 'traitee'}
                  >
                    <CheckCircle className="h-4 w-4 mr-1" />
                    Traitée
                  </Button>
                  <Button
                    variant="outline"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => handleUpdateStatus(selectedDemande.id, 'refusee')}
                    disabled={updatingStatus || selectedDemande.status === 'refusee'}
                  >
                    <XCircle className="h-4 w-4 mr-1" />
                    Refusée
                  </Button>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsModal(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
